import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Switch, Route, Redirect, useHistory } from 'react-router-dom';
import queryString from 'query-string';
import isEmpty from 'lodash/isEmpty';
import sortBy from 'lodash/sortBy';
import { usePostHog } from 'posthog-js/react'

import UpgradePricingModal from 'src/pricing/UpgradePricingModal';
import UpgradePaywall from 'src/pricing/UpgradePaywall';
import { NotificationMessage } from 'src/common/notification/message';
import { useNotificationMessage } from 'src/common/notification/message.hook';
import { FeatureFlagsContextProvider } from 'src/common/feature-flags/provider';
import {JoyRideProvider} from 'src/onboarding/joyride-context';
import ChangelogModal from 'src/changelog/ChangelogModal';
import {
  apiGetCurrentUser,
  apiGetOrganizations,
  apiGetAllJourneys,
  apiGetAllNodes,
  apiSwitchOrganizationBySlug,
  apiGetDestinations,
  apiGetCommsInboxUnreadCount,
  apiGetSubscription,
} from './utils/journeyApi';

import { useCurrentOrganization } from './store/organization';
import { useCurrentSubscription } from './store/subscription';

import { useCurrentUser } from './store/user';
import { useDestinations } from './store/destinations';

import OauthCallback from './common/OauthCallback';

import { PageJourneysEdit } from './pages/JourneysEdit';
import { PageStepEdit } from './pages/StepEdit';
import { PageSettings } from './pages/Settings';
import { PageIntegrationDetail } from './pages/IntegrationDetail';
import { PageJourneysContentCreationEdit } from './pages/JourneysContentCreationEdit';
import { PageJourneysContentCreationEditMobile } from './pages/JourneysContentCreationEditMobile';

import PageNewJourneyTemplate from 'src/dashboard/pages/new-journey-template.page';

import { DashboardHome } from 'src/dashboard/home';
import {HomeDashboardTab} from 'src/dashboard/home/Home';
import { DashboardLibrary } from 'src/dashboard/library';
import { DashboardInsights } from 'src/dashboard/insights';
import { DashboardSession } from 'src/dashboard/sessions';
import { DashboardSessionHome } from 'src/dashboard/sessions/home';
import { DashboardInbox } from 'src/dashboard/inbox';

import journeythatTheme from './journeythat_theme';
import { ThemeProvider } from '@material-ui/core/styles';
import JourneyUnfollow from './dashboard/JourneyUnfollow';
import JourneySessionUnfollow from './dashboard/JourneySessionUnfollow';

import { getBrandingColors } from 'src/brand-generation/colors';
import { useApplyBrandColors } from 'src/branding/set-brand-colors';
import { StepEditorContentConfigureStageTypeEnum } from 'src/editor/step-editor/content/configure/stage-type.enum.ts';
import AnalyticsHelper from 'src/common/helpers/analytics';
import { LocationChangeListener } from 'src/common/location-change-listener';

import { PageLayoutWithSidebar } from 'src/common/page/layout/with-sidebar';
import { OnboardingChecklist, useOnboardingChecklist } from 'src/common/components/onboarding-checklist';
import { DashboardLibraryContentUploadBubble } from 'src/dashboard/library/content/upload-bubble';
import { ShareThumbnailCodePreview } from 'src/common/components/share/thumbnail-code/preview';
import { DashboardInsightsJourneysJourney } from './dashboard/insights/journeys/journey';
import { DashboardInsightsJourneysJourneyVisitor } from './dashboard/insights/journeys/journey-visitor';
import AnalyticsDashboardPage from './comprehensive-analytics/AnalyticsDashboard';
import {AnalyticsOverview} from './comprehensive-analytics/AnalyticsOverview'

import { DashboardInsightsContent } from './dashboard/insights/content';
import { useDashboardInboxUserActions } from 'src/dashboard/inbox/user-actions.hook';
import { FixedLoadingSpinner } from 'src/common/components/loading-spinner';
import { useJourneys } from 'src/store/journeys';
import { FreeGatingUpgradeModal } from 'src/common/free-gating/FreeGatingUpgradeModal';
import { useFreeGatingStore, FreeGatingEnum } from 'src/common/free-gating/store';
import { getLinkToLogin } from './common/helpers/get-link-to-app-page';
import { trackSegmentEvent } from './utils/segment';
import TrialDiscountPopup from './components/TrialDiscountPopup';
import { useTrialDiscountPopup } from './hooks/useTrialDiscountPopup';

const CommandBarRouterLoader = () => {
  // This fucker is undefined if used inside the same component
  // where the router is defined.
  const history = useHistory();
  useEffect(() => {
    const routerFunc = (newUrl) => history.push(newUrl);
    window.CommandBar.addRouter(routerFunc);
  }, [history]);
  return null;
};

function App() {
  const getQueryParams = function (props) {
    return queryString.parse(props.location.search);
  };

  const posthog = usePostHog() 
  
  const [initializedPosthog,setInitializedPosthog] = useState(false);
  const [initializedTolt, setInitializedTolt] = useState(false);
  const [stripeLoaded, setStripeLoaded] = useState(false);

  const [fetched, setFetched] = useState(false);

  // Check if Stripe is loaded
  useEffect(() => {
    const checkStripeLoaded = () => {
      if (typeof window !== 'undefined' && window.Stripe) {
        setStripeLoaded(true);
      } else {
        // Retry after a short delay
        setTimeout(checkStripeLoaded, 100);
      }
    };
    
    checkStripeLoaded();
  }, []);

  const { isSubscribed, isOnTrial, trialExpired, isPro, isEnterprise, subscriptionPlanType, setCurrentSubscription } = useCurrentSubscription();

  const { currentOrganization, setCurrentOrganization, setFreeOrg } = useCurrentOrganization();
  const { currentUser, setCurrentUser, setOrganizations } = useCurrentUser();
  const setHasReachedFreeLimit = useJourneys((state) => state.setHasReachedFreeLimit);
  const destinationsStore = useDestinations();
  const setFreeGatingModalByType = useFreeGatingStore((state) => state.setFreeGatingModalByType);
  const { setSuccessNotification, setErrorNotification } = useNotificationMessage();
  const { setTotalUnreadMessageCount } = useDashboardInboxUserActions();
  
  // Trial discount popup hook
  const {
    shouldShowDiscountPopup,
    isDiscountPopupOpen,
    openDiscountPopup,
    closeDiscountPopup,
    daysSinceTrialExpiry
  } = useTrialDiscountPopup();
  
  // Onboarding checklist hook - called after currentOrganization is available
  const {
    isVisible: isChecklistVisible,
    isLoading: isChecklistLoading,
    hideChecklist,
    completeOnboarding,
  } = useOnboardingChecklist(currentOrganization?.slug);

  useEffect(() => {   
    if (currentUser && currentUser.uuid && !initializedPosthog) {
        try{
            posthog?.identify(currentUser.uuid, {
                userId: currentUser.uuid, ...(currentUser.name? {name: currentUser.name} : {})
            })
            setInitializedPosthog(true)
        }
        catch(err){
            return;
        }
      
    }
},[posthog, currentUser, initializedPosthog]);

useEffect(() => {   
  if (currentUser && currentUser.email && !initializedTolt) {
      try{
          window.tolt.signup(currentUser.email)
          setInitializedTolt(true)
      }
      catch(err){
          trackSegmentEvent('Tolt Referral Absent',{type:'Signup'})
          return;
      }
    
  }
},[ currentUser, initializedTolt]);

  const initalizeThirdParty = (user) => {
    if (window.CommandBar) {
      window.CommandBar.addSearch('journeys', (text) => {
        return apiGetAllJourneys(user.last_organization.id, { searchStr: text }).then(({ journeys }) => journeys);
      });
      window.CommandBar.addSearch('content', (text) => {
        return apiGetAllNodes(user.last_organization.id, { search_str: text }).then((x) => x.journey_nodes);
      });
      window.CommandBar.boot({
        id: user.uuid,
        organization: user.last_organization.slug,
      });
    }
  };

  useEffect(() => {
    const { access_grant_success, access_grant_failed } = queryString.parse(window.location.search);
    if (access_grant_success) {
      return setSuccessNotification('Email successfully added to whitelist');
    }
    // Failure reasons corresponds to a reason in AccessGrantFailureReason enum
    switch (access_grant_failed) {
      case '1': // unknown_user
        return setErrorNotification('You are not logged in.');
      case '2': // invalid_token
        return setErrorNotification('The link is invalid');
      case '3': // expired_token
        return setErrorNotification('The link has expired');
      case '4': // unknown_alias
        return setErrorNotification('The link is invalid');
      case '5': // unauthorized_user
        return setErrorNotification("You're not authorized to whitelist an email");
      case '6': // invalid_requester_email
        return setErrorNotification('The email to be whitelisted is not valid');
      case '7': // email_already_in_whitelist
        return setErrorNotification('The email is already in the whitelist');
      default:
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Check if we're on mobile-edit route (which uses static data and doesn't require auth)
    const isMobileEditRoute = window.location.pathname.includes('/journeys/mobile-edit/');
    
    // A little bit horrible yes, but this facility allows admins to jump to an arbitrary organization
    // without the issues of impersonation (a one time thing - as the user that is impersonated is not an admin
    // and thus re-impersonation can only occur after a log-out and fresh log-in.) via a query param.
    const queryParams = getQueryParams({ location: window.location });
    if (queryParams.switch_slug) {
      return apiSwitchOrganizationBySlug(queryParams.switch_slug).then(() => window.location.replace('/'));
    }

    // Skip API calls for mobile-edit route (it uses static data)
    if (isMobileEditRoute) {
      setFetched(true);
      return;
    }

    apiGetCurrentUser()
      .then((user) => {
        setCurrentUser(user);
        setCurrentOrganization(user.last_organization);
        setFreeOrg(user.organization);
        setHasReachedFreeLimit(user.organization,subscriptionPlanType);
        setFetched(true);

        const { is_locked } = user.last_organization;
        setFreeGatingModalByType(FreeGatingEnum.JOURNEYS, is_locked, is_locked);

        initalizeThirdParty(user);
        AnalyticsHelper.identifyUser(user);

        apiGetSubscription(user?.last_organization?.id).then(subscription => {
          setCurrentSubscription(subscription)
          setHasReachedFreeLimit(user.organization,subscription?.plan_type);
        })
        .catch(err => {
          // Subscription not found or error: fallback to trial information
          const org = user.last_organization;
          const trialSubscription = {
            plan_type: null,
            status: org.trial_start_at && !org.trial_expired ? 'trialing' : null,
            trial_start_at: org.trial_start_at,
            trial_end_at: org.trial_end_at,
            trial_expired: org.trial_expired,
          };
          setCurrentSubscription(trialSubscription);
          setHasReachedFreeLimit(user.organization, trialSubscription.plan_type);
        })

        apiGetDestinations()
          .then((data) => {
            destinationsStore.setDestinations(data.destinations);
          })
          .catch(() => {});

        apiGetCommsInboxUnreadCount().then(setTotalUnreadMessageCount);
      })
      .catch((err) => {
        if (err.error_type === 'UnauthorizedError') {
          window.location.href = getLinkToLogin();
        }
        // window.location.href = '/login';
        console.info(err);
      });

    apiGetOrganizations().then(({ organizations }) => {
      setOrganizations(sortBy(organizations, (o) => (o.name || o.slug).toLowerCase()));
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { applyBrandColors } = useApplyBrandColors();

  useEffect(() => {
    if (currentOrganization) {

      const brandingBaseColor = currentOrganization?.branding?.secondary_color;
      const { brandPrimaryColor, brandSecondaryColor, brandTextColor } = getBrandingColors(
        brandingBaseColor,
        currentOrganization?.branding?.force_generated_colors || false
      );
      applyBrandColors(
        brandPrimaryColor,
        brandSecondaryColor,
        brandTextColor,
        currentOrganization?.branding?.heading_typeface || 'sans_serif'
      );
    }
  }, [currentOrganization, applyBrandColors]);

  // Check if we're on mobile-edit route (which uses static data and doesn't require auth)
  const isMobileEditRoute = window.location.pathname.includes('/journeys/mobile-edit/');
  
  // For mobile-edit route, allow rendering even without user/org data (it uses static data)
  // For other routes, require user/org data to be loaded
  if (!isMobileEditRoute && (!fetched || isEmpty(currentUser) || isEmpty(currentOrganization))) {
    return <FixedLoadingSpinner />;
  }
  
  // Show paywall if trial expired and user is not subscribed (skip for mobile-edit route)
  if (!isMobileEditRoute && trialExpired && !isSubscribed) {
    return (
      <>
        <UpgradePaywall />
        {/* Trial Discount Popup - shown on top of paywall */}
        <TrialDiscountPopup
          isOpen={isDiscountPopupOpen}
          onClose={closeDiscountPopup}
          onUpgrade={openDiscountPopup}
          daysSinceExpiry={daysSinceTrialExpiry}
        />
      </>
    );
  }

  // Extract organization slug - for mobile-edit routes, get it from URL or use currentOrganization if available
  // For other routes, use currentOrganization.slug
  let organizationSlug;
  if (isMobileEditRoute) {
    // First try to use currentOrganization if it's been set (by static data initialization)
    if (currentOrganization?.slug) {
      organizationSlug = currentOrganization.slug;
    } else {
      // Extract from URL path: /<org>/journeys/mobile-edit/<uuid> or /journeys/mobile-edit/<uuid>
      const pathParts = window.location.pathname.split('/').filter(Boolean);
      const mobileEditIndex = pathParts.findIndex(part => part === 'journeys');
      if (mobileEditIndex > 0) {
        // Organization slug is before 'journeys'
        organizationSlug = pathParts[mobileEditIndex - 1];
      } else {
        // No organization in path, use a default fallback
        organizationSlug = 'default';
      }
    }
  } else {
    organizationSlug = currentOrganization?.slug;
  }
  
  // Safety check: ensure organizationSlug is never undefined
  if (!organizationSlug) {
    console.warn('organizationSlug is undefined, using fallback');
    organizationSlug = 'default';
  }
  
  const currentUserAndOrganization = {
    currentUser: currentUser,
    currentOrganization: currentOrganization,
  };

  return (
    <div className='main-app'>
      <Router>
        <LocationChangeListener />
        <FreeGatingUpgradeModal />
        <ThemeProvider theme={journeythatTheme}>
          <FeatureFlagsContextProvider value={currentOrganization?.feature_flags}>
            <JoyRideProvider >
             <ChangelogModal/>
             {
              ((!isSubscribed && !isOnTrial) || (isSubscribed && !isEnterprise)) &&
            <UpgradePricingModal />
            }
            <NotificationMessage />
            <DashboardLibraryContentUploadBubble />
            <ShareThumbnailCodePreview />
            
            {/* Trial Discount Popup - only shown when not on paywall */}
            {!(trialExpired && !isSubscribed) && (
              <TrialDiscountPopup
                isOpen={isDiscountPopupOpen}
                onClose={closeDiscountPopup}
                onUpgrade={openDiscountPopup}
                daysSinceExpiry={daysSinceTrialExpiry}
              />
            )}
            

            
                    <OnboardingChecklist
          isVisible={isChecklistVisible}
          onClose={hideChecklist}
          onComplete={completeOnboarding}
          organizationSlug={currentOrganization?.slug}
          isLoading={isChecklistLoading}
        />
            <Switch>
              <Route
                exact
                path={`/${organizationSlug}/journeys/edit/:uuid/section/:sectionId/step/new`}
                render={(props) => (
                  <PageJourneysEdit
                    uuid={props.match.params.uuid}
                    stepId={props.match.params.sectionId}
                    pageConfigureStage={StepEditorContentConfigureStageTypeEnum.NEW}
                  />
                )}
              />
              <Route
                path={`/${organizationSlug}/journeys/edit/:uuid/section/:sectionId/step/:stepId/configure/annotation`}
                render={(props) => (
                  <PageJourneysEdit
                    uuid={props.match.params.uuid}
                    stepId={props.match.params.sectionId}
                    nodeId={props.match.params.stepId}
                    pageConfigureStage={StepEditorContentConfigureStageTypeEnum.ANNOTATION}
                  />
                )}
              />
              <Route
                path={`/${organizationSlug}/journeys/edit/:uuid/section/:sectionId/step/:stepId/configure/behavior`}
                render={(props) => (
                  <PageJourneysEdit
                    uuid={props.match.params.uuid}
                    stepId={props.match.params.sectionId}
                    nodeId={props.match.params.stepId}
                    pageConfigureStage={StepEditorContentConfigureStageTypeEnum.BEHAVIOR}
                  />
                )}
              />
              <Route
                exact
                path={`/${organizationSlug}/journeys/edit/:uuid/section/:sectionId/step/:stepId/configure`}
                render={(props) => (
                  <PageJourneysEdit
                    uuid={props.match.params.uuid}
                    stepId={props.match.params.sectionId}
                    nodeId={props.match.params.stepId}
                    pageConfigureStage={StepEditorContentConfigureStageTypeEnum.CONFIGURE}
                  />
                )}
              />
              <Route
                exact
                path={`/${organizationSlug}/journeys/edit/:uuid/section/:sectionId/step/:stepId/document/:documentId`}
                render={(props) => (
                  <PageJourneysEdit
                    uuid={props.match.params.uuid}
                    stepId={props.match.params.sectionId}
                    nodeId={props.match.params.stepId}
                    documentId={props.match.params.documentId}
                  />
                )}
              />
              <Route
                path={`/${organizationSlug}/synced-steps/edit/:stepId/document/:documentId`}
                render={(props) => (
                  <PageStepEdit nodeId={props.match.params.stepId} documentId={props.match.params.documentId} />
                )}
              ></Route>
              <Route
                path={`/${organizationSlug}/synced-steps/edit/:stepId`}
                render={(props) => <PageStepEdit nodeId={props.match.params.stepId} />}
              ></Route>
              <Route
                path={`/${organizationSlug}/journeys/edit/:uuid/:editortabvalue/:sharetabvalue`}
                render={(props) => (
                  <PageJourneysEdit shareTabValue={props.match.params.sharetabvalue} uuid={props.match.params.uuid} />
                )}
              ></Route>
              <Redirect
                from={`/${organizationSlug}/journeys/edit/:uuid/share`}
                to={`/${organizationSlug}/journeys/edit/:uuid/share/link`}
              />
              <Redirect
                from={`/${organizationSlug}/journeys/edit/:uuid/section/:sectionId/step/:stepId`}
                to={`/${organizationSlug}/journeys/edit/:uuid/section/:sectionId/step/:stepId/configure`}
              />

              <Route
                path={`/${organizationSlug}/journeys/edit/:uuid/:tabvalue`}
                render={(props) => (
                  <PageJourneysEdit
                    uuid={props.match.params.uuid}
                    {...currentUserAndOrganization}
                    pageConfigureStage={StepEditorContentConfigureStageTypeEnum.BOARD}
                  />
                )}
              />
              <Redirect
                from={`/${organizationSlug}/journeys/edit/:uuid`}
                to={`/${organizationSlug}/journeys/edit/:uuid/content`}
              />
              <Route
                path={`/${organizationSlug}/journeys/cc-edit`}
                render={(props) => <PageJourneysContentCreationEdit history={props.history} />}
              ></Route>
              <Route
                path={`/${organizationSlug}/journeys/neue-edit/:uuid/:sectionPath`}
                render={(props) => (
                  <PageJourneysContentCreationEdit
                    history={props.history}
                    uuid={props.match.params.uuid}
                    sectionPath={props.match.params.sectionPath}
                  />
                )}
              ></Route>
              <Route
                path={`/${organizationSlug}/journeys/neue-edit/:uuid`}
                render={(props) => (
                  <PageJourneysContentCreationEdit history={props.history} uuid={props.match.params.uuid} />
                )}
              ></Route>
              <Route
                path={`/${organizationSlug}/journeys/mobile-edit/:uuid/:sectionPath`}
                render={(props) => (
                  <PageJourneysContentCreationEditMobile
                    history={props.history}
                    uuid={props.match.params.uuid}
                    sectionPath={props.match.params.sectionPath}
                  />
                )}
              ></Route>
              <Route
                path={`/${organizationSlug}/journeys/mobile-edit/:uuid`}
                render={(props) => (
                  <PageJourneysContentCreationEditMobile history={props.history} uuid={props.match.params.uuid} />
                )}
              ></Route>
              <Route
                path={`/${organizationSlug}/journeys/:page?`}
                render={(props) => (
                  <PageLayoutWithSidebar>
                    <DashboardHome activeListType={props.match.params.page} history={props.history} />
                  </PageLayoutWithSidebar>
                )}
              ></Route>
             
                                <Route
                path={`/${organizationSlug}/home`}
                >
                  <PageLayoutWithSidebar>
                    <HomeDashboardTab activeTabType='visitor-sessions'/>
                    </PageLayoutWithSidebar>
                
              </Route>
              <Route
                path={`/${organizationSlug}/inbox`}
                render={() => (
                  <PageLayoutWithSidebar>
                    <DashboardInbox listMessageType='all' />
                  </PageLayoutWithSidebar>
                )}
              />
              <Route
                path={`/${organizationSlug}/chatbots`}
                render={() => (
                  <PageLayoutWithSidebar>
                    <DashboardInbox listMessageType='all' />
                  </PageLayoutWithSidebar>
                )}
              />
              <Route
                path={`/${organizationSlug}/library/:page`}
                render={(props) => (
                  <PageLayoutWithSidebar>
                    <DashboardLibrary activeTab='library' activeListType={props.match.params.page} />
                  </PageLayoutWithSidebar>
                )}
              ></Route>
                  <Route
                path={`/${organizationSlug}/analytics/journeys/:journeyUuid`}
                render={(props) => (
                  <PageLayoutWithSidebar>
                    <AnalyticsDashboardPage
                      journeyUuid={props.match.params.journeyUuid}
                    />
                    </PageLayoutWithSidebar>
                )}
              ></Route>
                                 <Route
                path={`/${organizationSlug}/analytics/journeys`}
                >
                  <PageLayoutWithSidebar>
                    <AnalyticsOverview/>
                    </PageLayoutWithSidebar>
                
              </Route>
              <Route
                path={`/${organizationSlug}/insights/journeys/:journeyUuid/:sessionUuid/steps`}
                render={(props) => (
                  <PageLayoutWithSidebar>
                    <DashboardInsightsJourneysJourneyVisitor
                      journeyUuid={props.match.params.journeyUuid}
                      sessionUuid={props.match.params.sessionUuid}
                      activeTabType='steps'
                    />
                  </PageLayoutWithSidebar>
                )}
              ></Route>
              <Route
                path={`/${organizationSlug}/insights/journeys/:journeyUuid/:sessionUuid/visitor-sessions`}
                render={(props) => (
                  <PageLayoutWithSidebar>
                    <DashboardInsightsJourneysJourneyVisitor
                      journeyUuid={props.match.params.journeyUuid}
                      sessionUuid={props.match.params.sessionUuid}
                      activeTabType='visitor-sessions'
                    />
                  </PageLayoutWithSidebar>
                )}
              ></Route>
              <Route
                path={`/${organizationSlug}/insights/journeys/:journeyUuid/steps`}
                render={(props) => (
                  <PageLayoutWithSidebar>
                    <DashboardInsightsJourneysJourney
                      journeyUuid={props.match.params.journeyUuid}
                      activeTabType='steps'
                    />
                  </PageLayoutWithSidebar>
                )}
              ></Route>
              <Route
                path={`/${organizationSlug}/insights/journeys/:journeyUuid/visitor-sessions`}
                render={(props) => (
                  <PageLayoutWithSidebar>
                    <DashboardInsightsJourneysJourney
                      journeyUuid={props.match.params.journeyUuid}
                      activeTabType='visitor-sessions'
                    />
                  </PageLayoutWithSidebar>
                )}
              ></Route>
              <Route
                path={`/${organizationSlug}/insights/journeys/:journeyUuid/chatbot-sessions`}
                render={(props) => (
                  <PageLayoutWithSidebar>
                    <DashboardInsightsJourneysJourney
                      journeyUuid={props.match.params.journeyUuid}
                      activeTabType='chatbot-sessions'
                    />
                  </PageLayoutWithSidebar>
                )}
              ></Route>
              <Redirect
                from={`/${organizationSlug}/insights/journeys/:journeyUuid/:sessionUuid`}
                to={`/${organizationSlug}/insights/journeys/:journeyUuid/:sessionUuid/steps`}
              />
              <Redirect
                from={`/${organizationSlug}/insights/journeys/:journeyUuid`}
                to={`/${organizationSlug}/insights/journeys/:journeyUuid/steps`}
              />
              <Route
                path={`/${organizationSlug}/insights/journeys`}
                render={(props) => (
                  <PageLayoutWithSidebar>
                    <DashboardInsights activeListType='journeys' />
                  </PageLayoutWithSidebar>
                )}
              ></Route>
              <Route
                path={`/${organizationSlug}/insights/content/:contentUuid`}
                render={(props) => (
                  <PageLayoutWithSidebar>
                    <DashboardInsightsContent contentUuid={props.match.params.contentUuid} />
                  </PageLayoutWithSidebar>
                )}
              ></Route>
              <Route
                path={`/${organizationSlug}/insights/content`}
                render={() => (
                  <PageLayoutWithSidebar>
                    <DashboardInsights activeListType='content' />
                  </PageLayoutWithSidebar>
                )}
              ></Route>
              <Route
                path={`/${organizationSlug}/insights/visitors`}
                render={() => (
                  <PageLayoutWithSidebar>
                    <DashboardInsights activeListType='visitors' />
                  </PageLayoutWithSidebar>
                )}
              ></Route>
              <Route
                path={`/${organizationSlug}/insights/synced-steps`}
                render={() => (
                  <PageLayoutWithSidebar>
                    <DashboardInsights activeListType='synced-steps' />
                  </PageLayoutWithSidebar>
                )}
              ></Route>
               
                <Route
                path={`/${organizationSlug}/sessions/:sessionUuid`}
                render={(props) => (
                  <PageLayoutWithSidebar>
                    <DashboardSession sessionUuid={props.match.params.sessionUuid} activeListType='sessions' />
                  </PageLayoutWithSidebar>
                )}
              ></Route>
              <Route
                path={`/${organizationSlug}/sessions`}
                render={() => (
                  <PageLayoutWithSidebar>
                    <DashboardSessionHome activeListType='sessions' />
                  </PageLayoutWithSidebar>
                )}
              ></Route>
              <Route
                path={`/${organizationSlug}/integrations/:code`}
                render={(props) => (
                  <PageLayoutWithSidebar>
                    <PageIntegrationDetail
                      currentUser={currentUser}
                      currentOrganization={currentOrganization}
                      code={props.match.params.code}
                    />
                  </PageLayoutWithSidebar>
                )}
              />
              <Route path={`/${organizationSlug}/branding`}>
                <PageLayoutWithSidebar>
                  <PageSettings activeTab='branding' />
                </PageLayoutWithSidebar>
              </Route>

              <Route path={`/${organizationSlug}/integrations`}>
                <PageLayoutWithSidebar>
                  <PageSettings activeTab='integrations' />
                </PageLayoutWithSidebar>
              </Route>

              <Route path={`/${organizationSlug}/organization/members`}>
                <PageLayoutWithSidebar>
                  <PageSettings activeTab='members' />
                </PageLayoutWithSidebar>
              </Route>

              <Route path={`/${organizationSlug}/organization/settings`}>
                <PageLayoutWithSidebar>
                  <PageSettings activeTab='organization' />
                </PageLayoutWithSidebar>
              </Route>

              <Route path={`/${organizationSlug}/workspaces`}>
                <PageLayoutWithSidebar>
                  <PageSettings activeTab='workspaces' />
                </PageLayoutWithSidebar>
              </Route>

              <Route path={`/${organizationSlug}/theme`}>
                <PageLayoutWithSidebar>
                  <PageSettings activeTab='theme' />
                </PageLayoutWithSidebar>
              </Route>

              <Route path={`/${organizationSlug}/billing`}>
                <PageLayoutWithSidebar>
                  <PageSettings activeTab='billing' />
                </PageLayoutWithSidebar>
              </Route>

              <Route path={`/${organizationSlug}/account`}>
                <PageLayoutWithSidebar>
                  <PageSettings activeTab='account' />
                </PageLayoutWithSidebar>
              </Route>

              <Route path={`/${organizationSlug}/journey-behavior`}>
                <PageLayoutWithSidebar>
                  <PageSettings activeTab='behavior' />
                </PageLayoutWithSidebar>
              </Route>

              <Route path={`/${organizationSlug}/api-key-settings`}>
                <PageLayoutWithSidebar>
                  <PageSettings activeTab='apikey' />
                </PageLayoutWithSidebar>
              </Route>

              <Route path={`/templates`}>
                <PageNewJourneyTemplate />
              </Route>
              <Route
                path={`/oauth/:provider/callback`}
                render={(props) => <OauthCallback provider={props.match.params.provider} />}
              ></Route>

              <Route path={'/journey-unfollow/:journeyID'} component={JourneyUnfollow} />
              <Route path={'/journey-session-unfollow/:journeyID'} component={JourneySessionUnfollow} />
              

              <Redirect from={`/analytics/journeys/:journeyUuid`} to={`/${organizationSlug}/analytics/journeys/:journeyUuid`} />

              <Redirect from={`/analytics/journeys`} to={`/${organizationSlug}/analytics/journeys`}/>
                
              <Redirect
                from={`/${organizationSlug}/organization/settings`}
                to={`/${organizationSlug}/organization/settings/members`}
              />

              <Redirect from='/journeys/edit/:uuid' to={`/${organizationSlug}/journeys/edit/:uuid`} />
              <Redirect from='/journeys/cc-edit' to={`/${organizationSlug}/journeys/cc-edit`} />
              <Redirect from='/journeys/neue-edit/:uuid' to={`/${organizationSlug}/journeys/neue-edit/:uuid`} />
              <Redirect from='/journeys/mobile-edit/:uuid' to={`/${organizationSlug}/journeys/mobile-edit/:uuid`} />
              <Redirect from='/journeys/onboarding' to={`/${organizationSlug}/journeys/onboarding`} />
              <Redirect
                from='/journeys/new/choose-workspace'
                to={`/${organizationSlug}/journeys/new/choose-workspace`}
              />
              <Redirect from='/journeys/new' to={`/${organizationSlug}/journeys/new`} />
              <Redirect from='/journeys/:page?' to={`/${organizationSlug}/journeys/all`} />
              <Redirect from='/workspaces' to={`/${organizationSlug}/workspaces`} />
              <Redirect from='/synced-steps/edit/:stepId' to={`/${organizationSlug}/synced-steps/edit/:stepId`} />
              <Redirect from={`/${organizationSlug}/inbox`} to={`/${organizationSlug}/inbox/all`} />
              <Redirect from='/branding' to={`/${organizationSlug}/branding`} />
              <Redirect from='/sessions/:sessionUuid' to={`/${organizationSlug}/sessions/:sessionUuid`} />
              <Redirect from='/session/:sessionUuid' to={`/${organizationSlug}/sessions/:sessionUuid`} />
              <Redirect from='/session' to={`/${organizationSlug}/sessions`} />
              <Redirect from='/sessions' to={`/${organizationSlug}/sessions`} />
              <Redirect from='/billing' to={`/${organizationSlug}/billing`} />
              <Redirect from={`/${organizationSlug}/library`} to={`/${organizationSlug}/library/content`} />
              <Redirect from={`/${organizationSlug}/insights`} to={`/${organizationSlug}/insights/content`} />
              <Redirect from={`/insights`} to={`/${organizationSlug}/insights`} />
              <Redirect from={`/home`} to={`/${organizationSlug}/home`} />
               <Redirect to={`/${organizationSlug}/home`} />
              <Redirect from={`/home`} to={`/${organizationSlug}/journeys`} />
              <Redirect from={`/${organizationSlug}/home`} to={`/${organizationSlug}/journeys`} />
              <Redirect to={`/${organizationSlug}/journeys`} />
              <Redirect from={`/${organizationSlug}/templates`} to={`/templates`} />
            </Switch>
            </JoyRideProvider>
          </FeatureFlagsContextProvider>
        </ThemeProvider>
      </Router>
    </div>
  );
}

export default App;
